"use client";

import React from 'react';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { SocialLinks } from './SocialLinks';

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gray-900 text-white pt-16 pb-8">
      <div className="container">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 mb-12">
          {/* Platform */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{t('footer.platform')}</h3>
            <ul className="space-y-2">
              <li>
                <Link href="/how-it-works" className="text-gray-400 hover:text-white transition-colors">
                  {t('pages.howItWorks')}
                </Link>
              </li>
              <li>
                <Link href="/features" className="text-gray-400 hover:text-white transition-colors">
                  {t('pages.features')}
                </Link>
              </li>
              <li>
                <Link href="/pricing" className="text-gray-400 hover:text-white transition-colors">
                  {t('pages.pricing')}
                </Link>
              </li>
              <li>
                <Link href="/#upload-section" className="text-gray-400 hover:text-white transition-colors">
                  {t('common.uploadPitch')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Solutions */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{t('footer.solutions')}</h3>
            <ul className="space-y-2">
              <li>
                <Link href="/solutions" className="text-gray-400 hover:text-white transition-colors">
                  {t('footer.forStartups')}
                </Link>
              </li>
              <li>
                <Link href="/investors" className="text-gray-400 hover:text-white transition-colors">
                  {t('footer.forInvestors')}
                </Link>
              </li>
              <li>
                <Link href="/partners" className="text-gray-400 hover:text-white transition-colors">
                  {t('pages.partners')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Free tools. These are built and live but had NO inbound links
              anywhere in the header, footer or homepage — the only route in
              was the CTA on the 20 /investors/[segment] pages. Tools earn
              links and rank on their own, but only if they are reachable.

              Split out of Resources to mirror the header dropdowns (see the
              note above navItems in Header.tsx): the divide is by what the
              item asks of the visitor, not by content type, and "Free tools"
              does acquisition work as a label that "Resources" does not.
              Keep the two lists in sync with the header. */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{t('footer.freeTools')}</h3>
            <ul className="space-y-2">
              <li>
                <Link href="/match-preview" className="text-gray-400 hover:text-white transition-colors">
                  {t('footer.matchPreview')}
                </Link>
              </li>
              <li>
                <Link href="/tools/safe-calculator" className="text-gray-400 hover:text-white transition-colors">
                  {t('footer.safeCalculator')}
                </Link>
              </li>
              <li>
                <Link href="/tools/cold-email-generator" className="text-gray-400 hover:text-white transition-colors">
                  {t('footer.coldEmailGenerator')}
                </Link>
              </li>
              <li>
                <Link href="/pitch-deck-templates" className="text-gray-400 hover:text-white transition-colors">
                  {t('footer.pitchDeckTemplatesShort')}
                </Link>
              </li>
              <li>
                <Link href="/free-vc-list" className="text-gray-400 hover:text-white transition-colors">
                  {t('footer.freeVcList')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{t('footer.resources')}</h3>
            <ul className="space-y-2">
              <li>
                <Link href="/blog" className="text-gray-400 hover:text-white transition-colors">
                  {t('pages.blog')}
                </Link>
              </li>
              {/* The 2026 data report shipped with sitemap + llms-full.txt coverage
                  but no internal link anywhere, so Ahrefs flagged it as an orphan.
                  Same failure mode as the free tools above: published != reachable. */}
              <li>
                <Link href="/reports/state-of-early-stage-vc-2026" className="text-gray-400 hover:text-white transition-colors">
                  {t('footer.stateOfVcReport')}
                </Link>
              </li>
              <li>
                <Link href="/compare" className="text-gray-400 hover:text-white transition-colors">
                  {t('footer.compare')}
                </Link>
              </li>
              <li>
                <Link href="/faq" className="text-gray-400 hover:text-white transition-colors">
                  {t('pages.faq')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{t('footer.company')}</h3>
            <ul className="space-y-2">
              <li>
                <Link href="/about" className="text-gray-400 hover:text-white transition-colors">
                  {t('footer.aboutUs')}
                </Link>
              </li>
              <li>
                <Link href="/contact" className="text-gray-400 hover:text-white transition-colors">
                  {t('pages.contact')}
                </Link>
              </li>
              <li>
                <Link href="/trust" className="text-gray-400 hover:text-white transition-colors">
                  {t('pages.trust')}
                </Link>
              </li>
              <li>
                <Link href="/press" className="text-gray-400 hover:text-white transition-colors">
                  {t('pages.press')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{t('footer.legal')}</h3>
            <ul className="space-y-2">
              <li>
                <Link href="/terms" className="text-gray-400 hover:text-white transition-colors">
                  {t('pages.terms')}
                </Link>
              </li>
              <li>
                <Link href="/privacy" className="text-gray-400 hover:text-white transition-colors">
                  {t('pages.privacy')}
                </Link>
              </li>
              <li>
                <Link href="/cookies" className="text-gray-400 hover:text-white transition-colors">
                  {t('pages.cookies')}
                </Link>
              </li>
              <li>
                <Link href="/imprint" className="text-gray-400 hover:text-white transition-colors">
                  {t('pages.imprint')}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom: Copyright */}
        <div className="pt-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            {/* "SendVC, Inc." was false: the operator is Future Webservice, a
                German entity (see /imprint and organizationSchema.legalName).
                Naming it here corroborates the schema on every page instead of
                contradicting it. */}
            <p className="text-gray-400 text-sm" suppressHydrationWarning>
              &copy; {currentYear} SendVC &mdash; Future Webservice. {t('footer.rights')}
            </p>
            {/* A real crawlable anchor to llms.txt. The <link rel="alternate">
                in <head> (lib/seo-config.ts) only helps crawlers that parse
                head; this one is for the ones that just follow links. Plain <a>,
                not <Link>: /llms.txt is a static file, not a Next route. */}
            <a
              href="/llms.txt"
              className="text-gray-500 hover:text-gray-300 text-sm transition-colors"
            >
              llms.txt
            </a>
            {/* The generated long form. Same reasoning — a crawler that only
                follows links needs an anchor, not just a <link rel>. */}
            <a
              href="/llms-full.txt"
              className="text-gray-500 hover:text-gray-300 text-sm transition-colors"
            >
              llms-full.txt
            </a>
            <SocialLinks className="md:ml-auto" />
          </div>
        </div>
      </div>
    </footer>
  );
};

export { Footer };
